// SPDX-FileCopyrightText: 2026 Sequent Tech Inc <legal@sequentech.io>
//
// SPDX-License-Identifier: AGPL-3.0-only
// Monitoring types, in one place so they can be swapped for the generated
// GraphQL types once the Hasura actions are in the schema. Field names follow
// the Harvest JSON (snake_case); configuration documents mirror
// `sequent_core::monitoring::config`.
/**
 * How often the dashboard asks whether a new snapshot is live, when the
 * server does not report its snapshot interval (`refresh_seconds`).
 */
export var MONITORING_DEFAULT_REFRESH_MS = 30000;
/** The dashboard never asks more often than this, whatever the server reports. */
export var MONITORING_MIN_REFRESH_MS = 5000;
/** Frame height when the widget YAML sets none. */
export var DEFAULT_WIDGET_HEIGHT = 280;
/** Chart widths are requested in steps of this many pixels, so renders cache. */
export var WIDTH_BUCKET_PX = 40;
/**
 * No chart is drawn narrower than this: narrower, the renderer cuts every
 * label and draws marks of no width. A narrower frame scales the chart down.
 */
export var MIN_RENDER_WIDTH_PX = 360;
/** How long a resize settles before a new width is requested. */
export var WIDTH_DEBOUNCE_MS = 200;
export var GRID_COLUMNS = 12;
/** Result column the renderer orders by; the data table does not show it. */
export var POSITION_COLUMN = "position";
export var EMonitoringMode;
(function (EMonitoringMode) {
    EMonitoringMode["LEGACY"] = "LEGACY";
    EMonitoringMode["CONFIGURED"] = "CONFIGURED";
})(EMonitoringMode || (EMonitoringMode = {}));
/** Whether the dashboard is viewed or being edited; editing pauses polling. */
export var EMonitoringViewMode;
(function (EMonitoringViewMode) {
    EMonitoringViewMode["VIEW"] = "VIEW";
    EMonitoringViewMode["EDIT"] = "EDIT";
})(EMonitoringViewMode || (EMonitoringViewMode = {}));
export var EMonitoringCapability;
(function (EMonitoringCapability) {
    EMonitoringCapability["GRANTED"] = "GRANTED";
    EMonitoringCapability["DENIED"] = "DENIED";
})(EMonitoringCapability || (EMonitoringCapability = {}));
export var EScopeSelector;
(function (EScopeSelector) {
    EScopeSelector["REGION"] = "region";
    EScopeSelector["POST"] = "post";
    EScopeSelector["COUNTRY"] = "country";
})(EScopeSelector || (EScopeSelector = {}));
export var SCOPE_SELECTORS = [
    EScopeSelector.REGION,
    EScopeSelector.POST,
    EScopeSelector.COUNTRY,
];
export var ESelectorControl;
(function (ESelectorControl) {
    ESelectorControl["DROPDOWN"] = "dropdown";
    ESelectorControl["TOGGLE"] = "toggle";
})(ESelectorControl || (ESelectorControl = {}));
export var EDynamicOptions;
(function (EDynamicOptions) {
    EDynamicOptions["EVENT_DAYS"] = "event_days";
})(EDynamicOptions || (EDynamicOptions = {}));
export var EDataSource;
(function (EDataSource) {
    EDataSource["VOTER_TURNOUT"] = "voter_turnout";
    EDataSource["TEST_VOTING"] = "test_voting";
    EDataSource["ENROLLMENT_DECISIONS"] = "enrollment_decisions";
    EDataSource["VOTING_CREDENTIALS"] = "voting_credentials";
    EDataSource["POLL_STATUS"] = "poll_status";
    EDataSource["FINAL_TESTING_LOCKDOWN"] = "final_testing_lockdown";
    EDataSource["COUNTING_TRANSMISSION"] = "counting_transmission";
    EDataSource["VOTING_ENROLLMENT_ACTIVITY"] = "voting_enrollment_activity";
    EDataSource["ACCESS_SECURITY"] = "access_security";
    EDataSource["ATTACK_DETECTIONS"] = "attack_detections";
    EDataSource["HELPDESK"] = "helpdesk";
})(EDataSource || (EDataSource = {}));
export var EProducerState;
(function (EProducerState) {
    EProducerState["CONNECTED"] = "CONNECTED";
    EProducerState["NOT_CONNECTED"] = "NOT_CONNECTED";
})(EProducerState || (EProducerState = {}));
export var EWidgetRenderState;
(function (EWidgetRenderState) {
    EWidgetRenderState["RENDERED"] = "RENDERED";
    EWidgetRenderState["NOT_CONNECTED"] = "NOT_CONNECTED";
    EWidgetRenderState["NO_SNAPSHOT"] = "NO_SNAPSHOT";
    EWidgetRenderState["SCOPE_PENDING"] = "SCOPE_PENDING";
    EWidgetRenderState["RENDER_FAILED"] = "RENDER_FAILED";
    EWidgetRenderState["INVALID"] = "INVALID";
})(EWidgetRenderState || (EWidgetRenderState = {}));
/** Why a widget is SCOPE_PENDING, when Harvest says. */
export var EPendingReason;
(function (EPendingReason) {
    /** The settings were saved and the next count, with them, has not finished. */
    EPendingReason["SETTINGS_PENDING"] = "SETTINGS_PENDING";
})(EPendingReason || (EPendingReason = {}));
/** Why the portal itself could not show a widget, beside the server's states. */
export var EWidgetFailure;
(function (EWidgetFailure) {
    EWidgetFailure["REQUEST_FAILED"] = "REQUEST_FAILED";
})(EWidgetFailure || (EWidgetFailure = {}));
/**
 * What the server notes about a widget's figures, as
 * `sequent_core::monitoring::payload::Notice` names it. A code this build does
 * not know is still shown, as words.
 */
export var EMonitoringNotice;
(function (EMonitoringNotice) {
    EMonitoringNotice["UNREGISTERED_ATTEMPTS_AT_EVENT_SCOPE_ONLY"] = "UNREGISTERED_ATTEMPTS_AT_EVENT_SCOPE_ONLY";
    EMonitoringNotice["UNREGISTERED_ATTEMPTS_EXCLUDED"] = "UNREGISTERED_ATTEMPTS_EXCLUDED";
    /** Voting credentials: counted when the password is set, until their issue is recorded. */
    EMonitoringNotice["CREDENTIALS_ISSUED_WHEN_PASSWORD_SET"] = "CREDENTIALS_ISSUED_WHEN_PASSWORD_SET";
    /** Drawn with the live configuration: the snapshot's has not this widget yet. */
    EMonitoringNotice["CONFIG_NEWER_THAN_SNAPSHOT"] = "CONFIG_NEWER_THAN_SNAPSHOT";
    /** Drawn with the live configuration: the snapshot's is no longer kept. */
    EMonitoringNotice["CONFIG_AT_SNAPSHOT_UNAVAILABLE"] = "CONFIG_AT_SNAPSHOT_UNAVAILABLE";
})(EMonitoringNotice || (EMonitoringNotice = {}));
export var EColorScheme;
(function (EColorScheme) {
    EColorScheme["LIGHT"] = "LIGHT";
    EColorScheme["DARK"] = "DARK";
})(EColorScheme || (EColorScheme = {}));
export var EProblemSeverity;
(function (EProblemSeverity) {
    EProblemSeverity["ERROR"] = "ERROR";
    EProblemSeverity["WARNING"] = "WARNING";
})(EProblemSeverity || (EProblemSeverity = {}));
export var EMonitoringExportFormat;
(function (EMonitoringExportFormat) {
    EMonitoringExportFormat["CSV"] = "CSV";
    EMonitoringExportFormat["SQL"] = "SQL";
})(EMonitoringExportFormat || (EMonitoringExportFormat = {}));
export var EColumnKind;
(function (EColumnKind) {
    EColumnKind["TEXT"] = "text";
    EColumnKind["INTEGER"] = "integer";
    EColumnKind["NUMBER"] = "number";
})(EColumnKind || (EColumnKind = {}));
export var EConfigKind;
(function (EConfigKind) {
    EConfigKind["DASHBOARD"] = "dashboard";
    EConfigKind["WIDGET"] = "widget";
    EConfigKind["THEME"] = "theme";
    EConfigKind["SETTINGS"] = "settings";
})(EConfigKind || (EConfigKind = {}));
export var EConfigChange;
(function (EConfigChange) {
    EConfigChange["UPSERT"] = "UPSERT";
    EConfigChange["DELETE"] = "DELETE";
})(EConfigChange || (EConfigChange = {}));
export var EValidationResult;
(function (EValidationResult) {
    EValidationResult["VALID"] = "VALID";
    EValidationResult["INVALID"] = "INVALID";
})(EValidationResult || (EValidationResult = {}));
